// Plan vs actual: each budget line's dated periods with planned and actual
// amounts beside one another, so an overspend shows as a negative remainder.
// Reads the confirmed `/finance/budgets/reports/budget-vs-actual` endpoint
// (already used by the budget detail page) with the shared report chrome.
// Amounts are integer minor units — cents — never floats.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link } from "@tanstack/react-router";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
  type Budget,
  type BudgetVsActualRow,
  getBudgetVsActual,
  listBudgets,
} from "@/lib/api";
import { useAccountLabels } from "@/lib/labels";
import { formatPercent } from "./variance";
import { ReportError } from "./StatementTables";

const DISPLAY_CURRENCY = "USD";

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function money(cents: number): string {
  return formatMoney({ cents, currency: DISPLAY_CURRENCY });
}

/** Remaining plan as a percentage of the plan; `null` when the plan is zero. */
function remainingPercent(budgeted: number, actual: number): number | null {
  if (budgeted === 0) return null;
  return ((budgeted - actual) / Math.abs(budgeted)) * 100;
}

interface AccountGroup {
  accountId: string;
  rows: BudgetVsActualRow[];
  budgeted: number;
  actual: number;
}

export function BudgetVsActualPage() {
  const [budgets, setBudgets] = useState<Budget[] | null>(null);
  const [budgetId, setBudgetId] = useState("");
  const [report, setReport] = useState<BudgetVsActualRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const accountLabels = useAccountLabels();

  const loadBudgets = useCallback(() => {
    setError(null);
    setLoading(true);
    setBudgets(null);
    listBudgets()
      .then((list) => {
        setBudgets(list);
        const first = list.find((budget) => budget.is_active) ?? list[0];
        setBudgetId(first?.id ?? "");
      })
      .catch((cause: unknown) => setError(toErrorMessage(cause)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(loadBudgets, [loadBudgets]);

  const run = useCallback((id: string) => {
    if (!id) {
      setReport([]);
      return;
    }
    setReportError(null);
    setReport(null);
    getBudgetVsActual(id)
      .then(setReport)
      .catch((cause: unknown) => setReportError(toErrorMessage(cause)));
  }, []);

  useEffect(() => {
    run(budgetId);
  }, [budgetId, run]);

  const groups = useMemo<AccountGroup[]>(() => {
    if (!report) return [];
    const byAccount = new Map<string, AccountGroup>();
    for (const row of report) {
      const group = byAccount.get(row.chart_account_id) ?? {
        accountId: row.chart_account_id,
        rows: [],
        budgeted: 0,
        actual: 0,
      };
      group.rows.push(row);
      group.budgeted += row.budgeted_cents;
      group.actual += row.actual_cents;
      byAccount.set(row.chart_account_id, group);
    }
    return [...byAccount.values()];
  }, [report]);

  const totals = useMemo(
    () =>
      (report ?? []).reduce(
        (sum, row) => ({
          budgeted: sum.budgeted + row.budgeted_cents,
          actual: sum.actual + row.actual_cents,
        }),
        { budgeted: 0, actual: 0 },
      ),
    [report],
  );

  const noBudgets = budgets !== null && budgets.length === 0;

  return (
    <div className="grid gap-6">
      <PageHeader
        title="Budget vs actual"
        description="What you planned to spend next to what you actually spent, period by period."
        actions={
          <Button asChild variant="secondary">
            <Link to="/reports">All reports</Link>
          </Button>
        }
      />

      {error ? (
        <ReportError message={error} onRetry={loadBudgets} />
      ) : null}

      {budgets && budgets.length > 0 ? (
        <label
          htmlFor="bva-budget"
          className="surface grid max-w-md gap-1 rounded-2xl p-5 text-xs font-medium"
        >
          Plan
          <select
            id="bva-budget"
            className="select"
            value={budgetId}
            onChange={(event) => setBudgetId(event.target.value)}
            disabled={loading}
          >
            {budgets.map((budget) => (
              <option key={budget.id} value={budget.id}>
                {budget.name}
                {budget.is_active ? "" : " (inactive)"}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {noBudgets ? (
        <div className="surface rounded-2xl p-5 text-sm text-[var(--muted-foreground)]">
          No plans have been set up yet. Create one on the Budgets page and its
          plan-vs-actual report appears here.
        </div>
      ) : null}

      {reportError ? (
        <ReportError
          message={`Budget comparison unavailable: ${reportError}`}
          onRetry={() => run(budgetId)}
        />
      ) : null}

      {loading && budgets === null && !error ? (
        <div className="surface rounded-2xl p-5 text-sm text-[var(--muted-foreground)]">
          Loading…
        </div>
      ) : !noBudgets && report !== null ? (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Planned
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {money(totals.budgeted)}
              </p>
            </div>
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Actually spent
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {money(totals.actual)}
              </p>
            </div>
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Remaining
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {money(totals.budgeted - totals.actual)}
              </p>
            </div>
          </div>

          <div
            className="surface overflow-hidden rounded-2xl"
            data-testid="budget-vs-actual-table"
          >
            <div className="table-scroll">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead className="text-right">Planned</TableHead>
                    <TableHead className="text-right">Actually spent</TableHead>
                    <TableHead className="text-right">Remaining</TableHead>
                    <TableHead className="text-right">Remaining %</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groups.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        This plan has no periods with amounts yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    groups.map((group) => (
                      <Fragment key={group.accountId}>
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            className="bg-[var(--muted)] text-xs font-semibold tracking-widest uppercase text-[var(--muted-foreground)]"
                          >
                            {accountLabels.get(group.accountId) ?? "Account"}
                          </TableCell>
                        </TableRow>
                        {group.rows.map((row) => {
                          const remaining = row.budgeted_cents - row.actual_cents;
                          const percent = remainingPercent(
                            row.budgeted_cents,
                            row.actual_cents,
                          );
                          return (
                            <TableRow key={row.budget_period_id}>
                              <TableCell className="whitespace-nowrap">
                                {row.period_label}
                                <span className="block text-xs text-[var(--muted-foreground)]">
                                  {row.start_date} → {row.end_date}
                                </span>
                              </TableCell>
                              <TableCell className="text-right tabular-nums">
                                {money(row.budgeted_cents)}
                              </TableCell>
                              <TableCell className="text-right tabular-nums">
                                {money(row.actual_cents)}
                              </TableCell>
                              <TableCell
                                className={`text-right tabular-nums ${
                                  remaining < 0 ? "text-[var(--danger)]" : ""
                                }`}
                              >
                                {money(remaining)}
                              </TableCell>
                              <TableCell
                                className={`text-right tabular-nums ${
                                  remaining < 0 ? "text-[var(--danger)]" : ""
                                }`}
                              >
                                {formatPercent(percent)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        <TableRow>
                          <TableCell className="font-semibold">
                            Total {accountLabels.get(group.accountId) ?? "account"}
                          </TableCell>
                          <TableCell className="text-right font-semibold tabular-nums">
                            {money(group.budgeted)}
                          </TableCell>
                          <TableCell className="text-right font-semibold tabular-nums">
                            {money(group.actual)}
                          </TableCell>
                          <TableCell className="text-right font-semibold tabular-nums">
                            {money(group.budgeted - group.actual)}
                          </TableCell>
                          <TableCell className="text-right font-semibold tabular-nums">
                            {formatPercent(
                              remainingPercent(group.budgeted, group.actual),
                            )}
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
